import { EUserTypes, UserProjectRoles } from "constants/index";
import { isPerfEnv, isProductionEnv } from "utils/utils";

import { registerTour } from "../registry";
import {
  TOUR_READY,
  TOUR_TARGETS,
  tourReadySelector,
  tourSelector
} from "../selectors";

const gcRoles: string[] =
  (UserProjectRoles as Record<number, string[]>)[
    EUserTypes.GENERAL_CONTRACTOR
  ] ?? [];

const DASHBOARD_TOUR_PROJECT_ALLOWLIST = [
  "5c7227e2-a4fb-4283-be7b-6ce90f44a7e7",
  "36cdd01e-026a-4f1c-b7df-1a339cea55c3",
  "36cdd01e-026a-4f1c-b7df-1a339cea55c3",
  "d00244ce-cce6-4fcd-aab2-b7f289648559"
];

if (!isProductionEnv() && !isPerfEnv()) {
  registerTour({
    id: "gc-dashboard",
    version: 1,
    route: /^\/project\/[^/]+\/dashboard$/,
    roleGate: (role) => !!role && gcRoles.includes(role),
    readySelector: tourReadySelector(TOUR_READY.GC_DASHBOARD),
    featureFlag: "DASHBOARD_TOUR",
    projectIdAllowlist: DASHBOARD_TOUR_PROJECT_ALLOWLIST,
    steps: [
      {
        target: tourSelector(TOUR_TARGETS.ACTIONS_DUE_2_WEEKS),
        title: "Actions Due in 2 Weeks",
        content:
          "The 'Actions Due in 2 Weeks' provide key procurement items to focus on based on upcoming deadlines. Use the tabs to toggle between Materials and Submittals, or click 'View in Log' for the complete list with additional filtering options.",
        placement: "right",
        disableBeacon: true
      },
      {
        target: tourSelector(TOUR_TARGETS.RISK_UPDATES),
        title: "Risk Updates",
        content:
          "Use the risk quadrant to assess both short- and long-term risks, enabling proactive mitigation and effective risk management early in the project lifecycle. Items in the top-left quadrant, highlighted in red as high risk and due within two weeks, require immediate attention.",
        placement: "top"
      },
      {
        target: tourSelector(TOUR_TARGETS.ALERTS),
        title: "Alerts",
        content:
          "Use the Alerts panel to stay ahead of schedule-driven changes and address impacts to the procurement plan. Regularly reviewing alerts helps teams quickly understand schedule impact and respond proactively.",
        placement: "left"
      },
      {
        target: tourSelector(TOUR_TARGETS.PLANNING_DUE),
        title: "Planning Due",
        content:
          "The Planning Due section identifies submittals and materials that have not yet been linked to a schedule activity. Clicking 'Create Plan' allows you to establish those connections, enabling the system to calculate key milestones and track progress.",
        placement: "left"
      },
      {
        target: tourSelector(TOUR_TARGETS.NAV_SUBMITTALS),
        title: "Submittals",
        content:
          "At the top of the project dashboard, you'll find quick-access module tabs for Submittals, Materials, and Schedules. Click the Submittals module, to integrate submittals and link them with materials.",
        placement: "bottom"
      },
      {
        target: tourSelector(TOUR_TARGETS.NAV_MATERIALS),
        title: "Materials",
        content:
          "Click the Materials module, to create materials, request lead times from trade partners, and link schedule activities.",
        placement: "bottom"
      },
      {
        target: tourSelector(TOUR_TARGETS.NAV_SCHEDULES),
        title: "Schedules",
        content:
          "Click the Schedule module to upload schedules, run change impact analysis, and link activities to materials and submittals.",
        placement: "bottom"
      },
      {
        target: tourSelector(TOUR_TARGETS.NAV_SETTINGS),
        title: "Settings",
        content:
          "Click the 'Settings' tab to configure default durations for submittals and materials, create custom material templates, manage integrations, and invite users.",
        placement: "bottom"
      },
      {
        target: tourSelector(TOUR_TARGETS.NAV_REPORTS),
        title: "Reports",
        content:
          "Click the 'Reports' tab to create and manage reports for project meetings and stakeholder reviews.",
        placement: "bottom",
        optional: true
      }
    ]
  });
}
